import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Settings as SettingsIcon, Music, Share2, Copy, Check } from 'lucide-react';

const SocialFeed = ({ user }) => {
  const navigate = useNavigate();
  
  // Force component update
  console.log('SocialFeed loaded - version 2.0');
  const [activityFeed, setActivityFeed] = useState([]);
  const [following, setFollowing] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({ show_listening_activity: true });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      fetchActivityFeed();
      fetchFollowing();
      fetchPrivacySettings();
    }
  }, [user]);

  const fetchActivityFeed = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/activity/feed`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setActivityFeed(data);
      }
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
    }
  };

  const fetchFollowing = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/following`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setFollowing(data);
      }
    } catch (error) {
      console.error('Failed to fetch following:', error);
    }
  };

  const fetchPrivacySettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/privacy`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPrivacySettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch privacy settings:', error);
    }
  };

  const updatePrivacy = async (showActivity) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/privacy?show_activity=${showActivity}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        setPrivacySettings({ show_listening_activity: showActivity });
      }
    } catch (error) {
      console.error('Failed to update privacy:', error);
    }
  };

  const searchUsers = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/search?q=${encodeURIComponent(query)}`
      );
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  const followUser = async (userId) => {
    if (!user) {
      if (window.confirm('Login or create a free account to use this feature. Go to login page?')) {
        window.location.href = '/login';
      }
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/follow`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        fetchFollowing();
        setSearchResults([]);
        setSearchQuery('');
      }
    } catch (error) {
      console.error('Failed to follow user:', error);
    }
  };

  return (
    <div className="social-feed" style={{
      padding: '24px',
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(180deg, rgba(157, 78, 221, 0.05) 0%, rgba(255, 204, 0, 0.05) 50%, rgba(78, 221, 157, 0.05) 100%)',
      minHeight: '100vh'
    }}>
      <div className="social-header" style={{
        background: 'linear-gradient(135deg, #9D4EDD 0%, #FFD60A 100%)',
        padding: '24px 32px',
        borderRadius: '20px',
        marginBottom: '32px',
        boxShadow: '0 8px 32px rgba(157, 78, 221, 0.3)'
      }}>
        <h2 style={{
          fontSize: '36px',
          fontWeight: '800',
          color: '#fff',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
          margin: 0,
          marginBottom: '8px'
        }}>🌟 Social</h2>
        <p style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#fff',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.2)',
          margin: 0,
          opacity: 0.95
        }}>Vibe with Your Friends!</p>
        <button 
          className="icon-button"
          onClick={() => setShowSettings(!showSettings)}
        >
          <SettingsIcon size={20} />
        </button>
      </div>

      {showSettings && (
        <div className="social-settings">
          <h3>Privacy Settings</h3>
          <label className="privacy-toggle">
            <input
              type="checkbox"
              checked={privacySettings.show_listening_activity}
              onChange={(e) => updatePrivacy(e.target.checked)}
            />
            <span>Show my listening activity to friends</span>
          </label>
        </div>
      )}

      <div style={{
        background: 'linear-gradient(135deg, rgba(157, 78, 221, 0.15) 0%, rgba(199, 125, 255, 0.15) 100%)',
        border: '2px solid rgba(157, 78, 221, 0.3)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '32px',
        boxShadow: '0 8px 24px rgba(157, 78, 221, 0.2)'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#9D4EDD',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Users size={28} />
          Find Users to Follow
        </h3>
        
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchUsers(e.target.value);
            }}
            style={{
              width: '100%',
              padding: '16px 20px',
              fontSize: '16px',
              color: '#fff',
              background: 'rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(157, 78, 221, 0.4)',
              borderRadius: '16px',
              outline: 'none',
              transition: 'all 0.3s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#9D4EDD';
              e.target.style.background = 'rgba(0, 0, 0, 0.6)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(157, 78, 221, 0.4)';
              e.target.style.background = 'rgba(0, 0, 0, 0.4)';
            }}
          />
          {searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              background: '#1a1a1a',
              border: '2px solid rgba(157, 78, 221, 0.4)',
              borderRadius: '16px',
              padding: '8px',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 10,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)'
            }}>
              {searchResults.map(result => (
                <div 
                  key={result.id} 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    borderRadius: '12px',
                    marginBottom: '8px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'rgba(157, 78, 221, 0.2)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                >
                  <div>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#fff',
                      marginBottom: '4px'
                    }}>
                      {result.name}
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#999'
                    }}>
                      {result.email}
                    </div>
                  </div>
                  <button 
                    onClick={() => followUser(result.id)}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Follow
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Invite Friends Section - Compact */}
      <div style={{
        background: 'rgba(251, 146, 60, 0.08)',
        border: '1px solid rgba(251, 146, 60, 0.25)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <Share2 size={20} color="#fb923c" />
          <div>
            <div style={{
              fontSize: '15px',
              fontWeight: '600',
              color: '#fb923c',
              marginBottom: '2px'
            }}>
              Invite Friends
            </div>
            <div style={{
              fontSize: '13px',
              color: '#999'
            }}>
              Share MetaSoul® and connect through music
            </div>
          </div>
        </div>
        
        <button
          onClick={() => {
            navigator.clipboard.writeText(window.location.origin);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '8px',
            border: 'none',
            background: copied ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s',
            minWidth: '110px',
            justifyContent: 'center',
            whiteSpace: 'nowrap'
          }}
          onMouseOver={(e) => {
            if (!copied) {
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {copied ? (
            <>
              <Check size={16} />
              Copied!
            </>
          ) : (
            <>
              <Copy size={16} />
              Copy Link
            </>
          )}
        </button>
      </div>

      <div className="following-section" style={{
        background: 'linear-gradient(135deg, rgba(78, 221, 157, 0.2) 0%, rgba(34, 197, 94, 0.2) 100%)',
        border: '2px solid rgba(78, 221, 157, 0.4)',
        borderRadius: '20px',
        padding: '28px',
        marginBottom: '28px',
        boxShadow: '0 8px 24px rgba(78, 221, 157, 0.3)'
      }}>
        <h3 style={{
          fontSize: '26px',
          fontWeight: '800',
          color: '#4EDD9D',
          marginBottom: '24px',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
        }}>👥 Following ({following.length})</h3>
        {following.length === 0 ? (
          <p className="empty-state">You're not following anyone yet. Search above to find users!</p>
        ) : (
          <div className="following-list">
            {following.map(followedUser => (
              <div 
                key={followedUser.id} 
                className="following-item"
                onClick={() => navigate(`/user/${followedUser.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Users size={24} />
                <span style={{ textDecoration: 'underline' }}>{followedUser.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="activity-feed-section">
        <h3>Activity Feed</h3>
        {activityFeed.length === 0 ? (
          <p className="empty-state">
            No activity yet. Follow users to see what they're listening to!
          </p>
        ) : (
          <div className="activity-list">
            {activityFeed.map((activity, index) => (
              <div key={index} className="activity-item">
                <Music size={16} />
                <div>
                  <strong 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/user/${activity.user_id}`);
                    }}
                    style={{ 
                      cursor: 'pointer', 
                      textDecoration: 'underline',
                      color: '#4EDD9D'
                    }}
                  >
                    {activity.user_name}
                  </strong> listened to{' '}
                  <span className="song-name">{activity.song_title}</span> by {activity.artist}
                  <div className="activity-time">
                    {new Date(activity.listened_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialFeed;
